import { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import type { OutcomeDistributionEntry } from '../../lib/projectDashboard';
import './OutcomeDistributionChart.css';

export interface OutcomeDistributionChartProps {
  distribution: OutcomeDistributionEntry[];
}

const WIDTH = 400;
const HEIGHT = 260;
const RADIUS = 90;
const CX = 130;
const CY = HEIGHT / 2;

// Colorblind-safe palette (Tableau 10)
const PALETTE = [
  '#1f77b4',
  '#ff7f0e',
  '#2ca02c',
  '#d62728',
  '#9467bd',
  '#8c564b',
  '#e377c2',
  '#7f7f7f',
  '#bcbd22',
  '#17becf',
];

export function OutcomeDistributionChart({
  distribution,
}: OutcomeDistributionChartProps): JSX.Element {
  const svgRef = useRef<SVGSVGElement>(null);

  const totalAssignments = distribution.reduce((sum, d) => sum + d.count, 0);
  const isEmpty = distribution.length === 0 || totalAssignments === 0;

  useEffect(() => {
    if (!svgRef.current || isEmpty) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const pie = d3
      .pie<OutcomeDistributionEntry>()
      .value((d) => d.count)
      .sort(null);
    const arc = d3
      .arc<d3.PieArcDatum<OutcomeDistributionEntry>>()
      .innerRadius(RADIUS * 0.55)
      .outerRadius(RADIUS);

    const g = svg.append('g').attr('transform', `translate(${CX},${CY})`);

    const slices = pie(distribution);

    slices.forEach((slice, i) => {
      const color = PALETTE[i % PALETTE.length];

      g.append('path')
        .attr('d', arc(slice) ?? '')
        .attr('fill', color)
        .attr('stroke', 'var(--forge-theme-surface, #fff)')
        .attr('stroke-width', 2)
        .append('title')
        .text(`${slice.data.outcome.name}: ${slice.data.count}`);
    });

    // Legend
    const legend = svg
      .append('g')
      .attr('transform', `translate(${CX + RADIUS + 20}, ${CY - (distribution.length * 20) / 2})`);

    distribution.forEach((entry, i) => {
      const color = PALETTE[i % PALETTE.length];
      const ly = i * 22;

      legend
        .append('rect')
        .attr('x', 0)
        .attr('y', ly)
        .attr('width', 12)
        .attr('height', 12)
        .attr('fill', color)
        .attr('rx', 2);

      legend
        .append('text')
        .attr('x', 18)
        .attr('y', ly + 10)
        .attr('font-size', '11px')
        .attr('fill', 'var(--forge-theme-text-high, #212121)')
        .text(`${entry.outcome.name} (${entry.count})`);
    });
  }, [distribution, isEmpty]);

  return (
    <div className="outcome-distribution-chart">
      {isEmpty ? (
        <p className="outcome-distribution-empty forge-typography--body2">No assignments yet.</p>
      ) : (
        <svg
          ref={svgRef}
          width={WIDTH}
          height={HEIGHT}
          role="img"
          aria-label="Outcome distribution donut chart showing assignment counts per project outcome"
        />
      )}
    </div>
  );
}
